// Renders a verse share card onto a canvas: background (template or user
// photo), a legibility scrim, the verse text, its reference, and the app
// micro-branding. Exported at full social resolution; the preview just CSS-
// scales the same canvas.

export const CARD_SIZES = {
  square: { width: 1080, height: 1080, label: 'Post (1:1)' },
  story: { width: 1080, height: 1920, label: 'Story (9:16)' },
};

// Draw an image covering the whole canvas (like CSS object-fit: cover).
function drawCover(ctx, img, w, h) {
  const scale = Math.max(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
}

function wrapLines(ctx, text, maxWidth) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const attempt = line ? `${line} ${word}` : word;
    if (ctx.measureText(attempt).width <= maxWidth || !line) {
      line = attempt;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export function renderShareCard(canvas, {
  background,        // entry from SHARE_BACKGROUNDS (ignored when photo given)
  photo,             // HTMLImageElement or null
  verseText,
  verseRef,
  size = 'square',
}) {
  const { width: w, height: h } = CARD_SIZES[size] || CARD_SIZES.square;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, w, h);

  // 1. Background
  if (photo) {
    drawCover(ctx, photo, w, h);
  } else if (background) {
    background.draw(ctx, w, h);
  }

  // 2. Legibility scrim — stronger over photos, gentle over templates
  const scrimAlpha = photo ? 0.45 : 0.22;
  const scrim = ctx.createLinearGradient(0, 0, 0, h);
  scrim.addColorStop(0, `rgba(0,0,0,${scrimAlpha * 0.7})`);
  scrim.addColorStop(0.45, `rgba(0,0,0,${scrimAlpha})`);
  scrim.addColorStop(1, `rgba(0,0,0,${scrimAlpha * 1.5})`);
  ctx.fillStyle = scrim;
  ctx.fillRect(0, 0, w, h);

  // 3. Verse text — auto-size to fit the safe area
  const quoted = `“${verseText.trim()}”`;
  const maxWidth = w * 0.82;
  const maxTextHeight = h * (size === 'story' ? 0.46 : 0.52);
  let fontSize = size === 'story' ? 76 : 68;
  let lines;
  let lineHeight;
  do {
    ctx.font = `600 ${fontSize}px Georgia, 'Times New Roman', serif`;
    lines = wrapLines(ctx, quoted, maxWidth);
    lineHeight = fontSize * 1.32;
    if (lines.length * lineHeight <= maxTextHeight) break;
    fontSize -= 3;
  } while (fontSize > 26);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 3;

  const blockHeight = lines.length * lineHeight;
  const centerY = h * (size === 'story' ? 0.46 : 0.47);
  let y = centerY - blockHeight / 2 + lineHeight / 2;
  for (const line of lines) {
    ctx.fillText(line, w / 2, y);
    y += lineHeight;
  }

  // 4. Reference — small caps style below the verse
  const refSize = Math.max(30, Math.round(fontSize * 0.52));
  ctx.font = `700 ${refSize}px Inter, -apple-system, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.shadowBlur = 10;
  ctx.fillText(verseRef.toUpperCase(), w / 2, y + refSize * 0.9);

  // 5. Micro-branding, centered at the very bottom
  ctx.shadowBlur = 6;
  ctx.font = `500 ${Math.round(w * 0.022)}px Inter, -apple-system, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.78)';
  ctx.fillText('recorded on LineageBible app', w / 2, h - Math.round(w * 0.045));

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

export function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Could not export image'))), 'image/png');
  });
}

export function buildShareCaption(verseRef) {
  return `I just recorded ${verseRef} for my family on Lineage Bible \u{1F399}️ lineagebible.com`;
}

function cardFileName(verseRef) {
  return `${verseRef.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-lineage-bible.png`;
}

// Download the rendered card as a PNG file.
export async function downloadCard(canvas, verseRef) {
  const blob = await canvasToBlob(canvas);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = cardFileName(verseRef);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// Share via the native sheet where available; otherwise download the PNG.
// Returns 'shared' | 'downloaded' | 'cancelled'.
export async function shareCard(canvas, verseRef) {
  const blob = await canvasToBlob(canvas);
  const file = new File([blob], cardFileName(verseRef), { type: 'image/png' });
  const caption = buildShareCaption(verseRef);

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text: caption });
      return 'shared';
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled';
      // fall through to download on any other share failure
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return 'downloaded';
}
